// xlsx in and out of a workbook's grid (design 02 5.3, 8.3, 12.1). The
// file is read and written in the browser (xlsx/); this module turns it
// into sheets to create and cells to send, and the workbook into a file.
//
// Import: each sheet of the file becomes a new sheet (a name that is taken
// gets " (2)", and formulas that pointed at it follow, see renameRefs);
// cells go up 5000 per PUT; defined names through the names API (a name
// that is taken is left out and said so); shared formulas are spread out by
// the engine's translate. What the file had that is not taken (charts,
// pivots, ...) is listed for the person.
//
// Export: the grid sheets with formulas and their computed values. A
// formula Excel cannot compute (tensor names and the built-in names 거래 ·
// 지출 · 수입 · 예산 · 사람 · 월 · 분류 · 결제수단, over=, by=, along=,
// horizon=, rows=, cols= and part=, our functions TOTAL, YM, MEAN,
// shift/cumsum/diff, predtrend and the named linear algebra (contract,
// solve, inverse, det, lstsq, svd, eig, qr, cholesky), names that use
// them) is written as its value with
// the original formula in a note. Then a 거래 sheet (an Excel table) and an
// 예산 sheet (months x top-level categories), and the sheets the caller
// adds (차원 and the saved views, viewsheets.ts).
import type { CalcClient } from '../calc/calcClient'
import type { CalcValue } from '../calc/protocol'
import type { TensorCellInput, TensorSheet, TensorSheetInput, TensorStyle } from '../api'
import { readXlsx } from '../xlsx/read'
import { type WBook, type WCell, type WSheet, writeXlsx } from '../xlsx/write'
import {
  COL_WIDTH_MAX,
  COL_WIDTH_MIN,
  RESERVED_NAMES,
  type XName,
  type XSheet,
  type XStyle,
  colName,
  sheetNameValid,
} from '../xlsx/model'
import { type BuiltinData, LEDGER_COLUMNS } from './builtins'
import { FLAG } from '../calc/protocol'
import { cellKey, keyCol, keyRow } from './model'

// ---- import ----

export interface ImportSheet {
  /** the name in the file */
  original: string
  /** the name it gets here */
  name: string
  cells: TensorCellInput[]
  patch: TensorSheetInput | null
}

export interface ImportPlan {
  sheets: ImportSheet[]
  names: XName[]
  skipped: string[]
}

/** A sheet name not in taken (lower case): "이름", "이름 (2)", … (31 characters at most). */
export function uniqueSheetName(name: string, taken: Set<string>): string {
  if (!taken.has(name.toLowerCase()) && sheetNameValid(name)) return name
  const base = [...name].slice(0, 25).join('').trim() || '시트'
  for (let k = 2; ; k++) {
    const n = `${base} (${k})`
    if (!taken.has(n.toLowerCase()) && sheetNameValid(n)) return n
  }
}

function styleOf(s: XStyle | undefined): TensorStyle | undefined {
  if (!s) return undefined
  const out: TensorStyle = {}
  if (s.nf) out.nf = s.nf
  if (s.b) out.b = true
  if (s.i) out.i = true
  if (s.u) out.u = true
  if (s.fg && /^#[0-9a-fA-F]{6}$/.test(s.fg)) out.fg = s.fg
  if (s.bg && /^#[0-9a-fA-F]{6}$/.test(s.bg)) out.bg = s.bg
  if (s.al) out.al = s.al
  if (s.wrap) out.wrap = true
  return Object.keys(out).length ? out : undefined
}

function sheetPatch(s: XSheet): TensorSheetInput | null {
  const p: TensorSheetInput = {}
  const widths: Record<string, number> = {}
  for (const [c, w] of Object.entries(s.colWidths))
    widths[c] = Math.min(COL_WIDTH_MAX, Math.max(COL_WIDTH_MIN, Math.round(w)))
  if (Object.keys(widths).length) p.col_widths = widths
  if (s.frozenRows) p.frozen_rows = s.frozenRows
  if (s.frozenCols) p.frozen_cols = s.frozenCols
  return Object.keys(p).length ? p : null
}

const MARK = '\u0001'

/**
 * Reads a file into what to create. `taken`: the sheet and defined names
 * the workbook has (any case). `scratch` makes a calc worker of its own
 * for formulas that point at a renamed sheet (only when one is renamed).
 */
export async function planImport(
  bytes: Uint8Array,
  calc: Pick<CalcClient, 'translateAll'>,
  taken: { sheets: string[]; names: string[] },
  scratch: () => Pick<CalcClient, 'run' | 'terminate'>,
): Promise<ImportPlan> {
  // shared formulas: marked while reading, moved by the engine after
  const moves: { formula: string; drow: number; dcol: number }[] = []
  const book = await readXlsx(bytes, {
    translate: (formula, drow, dcol) => {
      moves.push({ formula: '=' + formula, drow, dcol })
      return `${MARK}${moves.length - 1}${MARK}`
    },
  })
  const skipped = new Set(book.skipped)
  const moved = moves.length ? await calc.translateAll(moves) : []
  const unmark = (input: string): string => {
    const m = /^=\u0001(\d+)\u0001$/.exec(input)
    if (!m) return input
    const t = moved[Number(m[1])]
    if (t == null) {
      skipped.add('공유 수식 일부(값으로 가져옴)')
      return ''
    }
    return t
  }
  const takenLow = new Set(taken.sheets.map((s) => s.toLowerCase()))
  for (const s of book.sheets) takenLow.add(s.name.toLowerCase())
  const sheets: ImportSheet[] = []
  const used = new Set(taken.sheets.map((s) => s.toLowerCase()))
  for (const s of book.sheets) {
    const name = used.has(s.name.toLowerCase()) ? uniqueSheetName(s.name, takenLow) : s.name
    used.add(name.toLowerCase())
    takenLow.add(name.toLowerCase())
    const cells: TensorCellInput[] = []
    for (const c of s.cells) {
      const input = unmark(c.input)
      const style = styleOf(c.style)
      if (input === '' && !style) continue
      cells.push({ row: c.row, col: c.col, input, ...(style ? { style } : {}) })
    }
    sheets.push({ original: s.name, name, cells, patch: sheetPatch(s) })
  }
  const namesTaken = new Set(taken.names.map((n) => n.toLowerCase()))
  const names: XName[] = []
  for (const n of book.names) {
    if (namesTaken.has(n.name.toLowerCase()) || RESERVED_NAMES.includes(n.name)) {
      skipped.add(`이미 있는 이름 정의(${n.name})`)
      continue
    }
    names.push({ ...n })
  }
  if (sheets.some((s) => s.name !== s.original)) await renameRefs(sheets, names, scratch())
  return { sheets, names, skipped: [...skipped] }
}

/**
 * Formulas of the file name its sheets by their names in the file; renamed
 * ones are rewritten with the engine's own rename (a calc worker of its
 * own: the file's sheets and formulas, then rename_sheet, then read back).
 */
async function renameRefs(sheets: ImportSheet[], names: XName[], w: Pick<CalcClient, 'run' | 'terminate'>): Promise<void> {
  try {
    const tag = Math.random().toString(36).slice(2, 8)
    const NAMES_SHEET = `names_${tag}`
    const cmds: Parameters<typeof w.run>[0] = [{ cmd: 'reset' }]
    sheets.forEach((s) => cmds.push({ cmd: 'add_sheet', name: s.original, recalc: false }))
    cmds.push({ cmd: 'add_sheet', name: NAMES_SHEET, recalc: false })
    sheets.forEach((s) => {
      const formulas = s.cells.filter((c) => c.input.startsWith('=')).map((c) => [s.original, c.row, c.col, c.input] as [string, number, number, string])
      if (formulas.length) cmds.push({ cmd: 'set_cells', cells: formulas, recalc: false })
    })
    if (names.length)
      cmds.push({ cmd: 'set_cells', cells: names.map((n, i) => [NAMES_SHEET, i, 0, '=' + n.formula] as [string, number, number, string]), recalc: false })
    // the renames, through temporary names again (a swap of two names)
    sheets.forEach((s, i) => {
      if (s.name !== s.original) cmds.push({ cmd: 'rename_sheet', from: s.original, to: `tmp_${tag}_${i}`, recalc: false })
    })
    sheets.forEach((s, i) => {
      if (s.name !== s.original) cmds.push({ cmd: 'rename_sheet', from: `tmp_${tag}_${i}`, to: s.name, recalc: false })
    })
    const reads = sheets.map((s) => ({ cmd: 'get' as const, sheet: s.name, r1: 0, c1: 0, r2: 1_048_575, c2: 16_383 }))
    const answers = await w.run([...cmds, ...reads, { cmd: 'get', sheet: NAMES_SHEET, r1: 0, c1: 0, r2: Math.max(0, names.length - 1), c2: 0 }])
    const got = answers.slice(cmds.length)
    sheets.forEach((s, i) => {
      const a = got[i]
      if (!a?.ok || !a.cells) return
      const byKey = new Map(a.cells.map(([r, c, input]) => [cellKey(r, c), input]))
      for (const c of s.cells) {
        if (!c.input.startsWith('=')) continue
        const v = byKey.get(cellKey(c.row, c.col))
        if (v !== undefined) c.input = v
      }
    })
    const n = got[sheets.length]
    if (n?.ok && n.cells) for (const [r, , input] of n.cells) if (names[r] && input.startsWith('=')) names[r]!.formula = input.slice(1)
  } finally {
    w.terminate()
  }
}

// ---- export ----

const BUILTIN = new Set([...RESERVED_NAMES])
/** The engine's own functions (calc_own_funcs in server/src/calc/funcs.c), in any case: none is Excel's. */
export const OWN_FUNCS = new Set([
  'MEAN',
  'SHIFT',
  'CUMSUM',
  'DIFF',
  'TOTAL',
  'YM',
  'PREDTREND',
  // the named linear algebra (design 02 6.1)
  'CONTRACT',
  'SOLVE',
  'INVERSE',
  'DET',
  'LSTSQ',
  'SVD',
  'EIG',
  'QR',
  'CHOLESKY',
])

/** The parts of a formula outside "strings" (and 'sheet names'). */
function outsideQuotes(f: string): string {
  return f.replace(/"(?:[^"]|"")*"/g, '""').replace(/'(?:[^']|'')*'!/g, 'S!')
}

/**
 * Whether Excel can compute a formula: false for our names and extensions.
 * `ours`: defined names (lower case) that are not Excel's either.
 */
export function excelCanCompute(formula: string, ours: Set<string> = new Set()): boolean {
  const f = outsideQuotes(formula)
  if (/\b(over|by|along|horizon|rows|cols|part)\s*=/i.test(f)) return false
  for (const m of f.matchAll(/([\p{L}_][\p{L}\p{N}_.]*)\s*(\(|\[)?/gu)) {
    const word = m[1] ?? ''
    const base = word.split('.')[0] ?? word
    if (m[2] === '[') return false // 지출[사람=엄마]
    if (m[2] === '(') {
      // our functions (shift, TOTAL, predtrend, inverse, …); sum(x, over=…) is caught above
      if (OWN_FUNCS.has(word.toUpperCase())) return false
      continue
    }
    if (BUILTIN.has(base) || ours.has(base.toLowerCase())) return false
  }
  return true
}

export interface ExportSheetData {
  sheet: TensorSheet
  /** the cells: key -> input, style */
  cells: Map<number, { input: string; style: TensorStyle | null }>
  values: Map<number, [CalcValue, number]>
}

function xvalue(v: CalcValue | undefined): WCell['value'] {
  if (v === undefined) return null
  return v
}

function uniqueIn(name: string, used: Set<string>): string {
  const n = uniqueSheetName(name, used)
  used.add(n.toLowerCase())
  return n
}

/**
 * The .xlsx of a workbook's grid and its 거래 and 예산. `more`: names that
 * are not Excel's (input tensors, dimensions people made) and sheets put
 * after 예산 (차원, the saved views: viewsheets.ts).
 */
export async function exportBook(
  sheets: ExportSheetData[],
  names: { name: string; formula: string }[],
  data: BuiltinData,
  more: { names?: string[]; sheets?: WSheet[] } = {},
): Promise<Uint8Array> {
  // names Excel cannot compute: left out, and formulas using them are values
  const ours = new Set<string>((more.names ?? []).map((n) => n.toLowerCase()))
  let grew = true
  while (grew) {
    grew = false
    for (const n of names)
      if (!ours.has(n.name.toLowerCase()) && !excelCanCompute(n.formula, ours)) {
        ours.add(n.name.toLowerCase())
        grew = true
      }
  }
  const used = new Set<string>()
  const out: WSheet[] = []
  for (const d of sheets) {
    used.add(d.sheet.name.toLowerCase())
    const cells: WCell[] = []
    const keys = new Set([...d.cells.keys(), ...d.values.keys()])
    for (const k of [...keys].sort((a, b) => a - b)) {
      const row = keyRow(k)
      const col = keyCol(k)
      const c = d.cells.get(k)
      const v = d.values.get(k)
      const style = c?.style ?? undefined
      const input = c?.input ?? ''
      if (input.startsWith('=')) {
        const formula = input.slice(1)
        if (excelCanCompute(formula, ours) && !((v?.[1] ?? 0) & FLAG.tensor)) {
          const cell: WCell = { row, col, formula, value: xvalue(v?.[0]) }
          if ((v?.[1] ?? 0) & FLAG.spill) cell.spill = spillSize(d.values, row, col)
          if (style) cell.style = style
          cells.push(cell)
        } else {
          cells.push({ row, col, value: xvalue(v?.[0]), note: `tensor 수식: ${input}`, ...(style ? { style } : {}) })
        }
      } else if (input !== '' || v || style) {
        cells.push({ row, col, value: xvalue(v?.[0]), ...(style ? { style } : {}) })
      }
    }
    out.push({
      name: d.sheet.name,
      cells,
      colWidths: d.sheet.col_widths,
      frozenRows: d.sheet.frozen_rows,
      frozenCols: d.sheet.frozen_cols,
    })
  }
  // 거래: an Excel table
  const ledgerName = uniqueIn('거래', used)
  const lc: WCell[] = LEDGER_COLUMNS.map((h, col) => ({ row: 0, col, value: h, style: { b: true } }))
  data.table.forEach((r, i) =>
    r.forEach((v, col) => {
      if (v === null) return
      const cell: WCell = { row: i + 1, col, value: v }
      if (col === 0) cell.style = { nf: 'yyyy-mm-dd' }
      if (col === 2) cell.style = { nf: '#,##0' }
      lc.push(cell)
    }),
  )
  out.push({
    name: ledgerName,
    cells: lc,
    colWidths: { '0': 96, '4': 140, '7': 200 },
    frozenRows: 1,
    tables: [
      {
        name: 'tensor거래',
        ref: `A1:${colName(LEDGER_COLUMNS.length - 1)}${Math.max(2, data.table.length + 1)}`,
        columns: [...LEDGER_COLUMNS],
      },
    ],
  })
  // 예산: months down, top-level categories across
  const budgetName = uniqueIn('예산', used)
  const C = data.dims.분류.length
  const bc: WCell[] = [{ row: 0, col: 0, value: '월', style: { b: true } }]
  data.dims.분류.forEach((c, j) => bc.push({ row: 0, col: j + 1, value: c, style: { b: true } }))
  let r = 1
  data.dims.월.forEach((m, i) => {
    const line = data.예산.slice(i * C, i * C + C)
    if (!line.some((v) => v > 0)) return
    bc.push({ row: r, col: 0, value: m })
    line.forEach((v, j) => {
      if (v > 0) bc.push({ row: r, col: j + 1, value: v, style: { nf: '#,##0' } })
    })
    r++
  })
  out.push({ name: budgetName, cells: bc, frozenRows: 1, frozenCols: 1 })
  for (const s of more.sheets ?? []) out.push({ ...s, name: uniqueIn(s.name, used) })
  const book: WBook = {
    sheets: out,
    names: names.filter((n) => !ours.has(n.name.toLowerCase())).map((n) => ({ name: n.name, formula: n.formula })),
  }
  return writeXlsx(book)
}

/** How far a spill anchor's array reaches (the spilled cells right and below). */
function spillSize(values: Map<number, [CalcValue, number]>, row: number, col: number): { rows: number; cols: number } {
  const spilled = (r: number, c: number) => ((values.get(cellKey(r, c))?.[1] ?? 0) & FLAG.spilled) !== 0
  let cols = 1
  while (spilled(row, col + cols)) cols++
  let rows = 1
  while (spilled(row + rows, col)) rows++
  return { rows, cols }
}
